import type {
  CourseRegistrationEnrollmentChildrenFilterViewModel,
  CourseRegistrationEnrollmentChildViewModel,
  GetApiV1CourseregistrationEnrollmentGridChildrenQueryParams,
} from '@/shared/api/generated';
import { useGetApiV1CourseregistrationEnrollmentGridChildren } from '@/shared/api/generated';

const CHILDREN_PAGE_SIZE = 10;

function filterToParams(
  filter: CourseRegistrationEnrollmentChildrenFilterViewModel | undefined,
  page: number,
): GetApiV1CourseregistrationEnrollmentGridChildrenQueryParams {
  const params: GetApiV1CourseregistrationEnrollmentGridChildrenQueryParams = {
    pageNumber: page,
    pageSize: CHILDREN_PAGE_SIZE,
  };
  if (!filter) return params;

  if (filter.classStudentId !== undefined) params.ClassStudentId = filter.classStudentId;
  if (filter.poloId !== undefined && filter.poloId !== null) params.PoloId = filter.poloId;
  if (filter.filter !== undefined) params.Filter = filter.filter;
  if (filter.companyId !== undefined && filter.companyId !== null) {
    params.CompanyId = filter.companyId;
  }
  if (filter.status !== undefined) params.Status = filter.status;
  if (filter.studentName) params.StudentName = filter.studentName;
  if (filter.email) params.Email = filter.email;
  if (filter.cpf) params.Cpf = filter.cpf;
  if (filter.documents !== undefined) params.Documents = filter.documents;
  if (filter.documentStartDate) params.DocumentStartDate = filter.documentStartDate;
  if (filter.documentEndDate) params.DocumentEndDate = filter.documentEndDate;

  return params;
}

type ChildrenResponse = {
  children: readonly CourseRegistrationEnrollmentChildViewModel[];
  totalCount: number;
  totalPages: number;
};

/**
 * O swagger do Kubb declara a resposta como `Child[]`, mas o backend pode
 * envelopar em `{ items, totalCount, totalPages }`. Aceita ambos os formatos
 * e extrai metadados de paginação quando disponíveis.
 */
function parseChildrenResponse(data: unknown, pageSize: number): ChildrenResponse {
  if (Array.isArray(data)) {
    const children = data as readonly CourseRegistrationEnrollmentChildViewModel[];
    return { children, totalCount: children.length, totalPages: 1 };
  }

  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;

    const children = (() => {
      if (Array.isArray(obj['items']))
        return obj['items'] as readonly CourseRegistrationEnrollmentChildViewModel[];
      if (Array.isArray(obj['data']))
        return obj['data'] as readonly CourseRegistrationEnrollmentChildViewModel[];
      if (Array.isArray(obj['children']))
        return obj['children'] as readonly CourseRegistrationEnrollmentChildViewModel[];
      return [] as readonly CourseRegistrationEnrollmentChildViewModel[];
    })();

    const totalCount =
      typeof obj['totalCount'] === 'number'
        ? obj['totalCount']
        : typeof obj['totalPeopleCount'] === 'number'
          ? obj['totalPeopleCount']
          : children.length;

    const totalPages =
      typeof obj['totalPages'] === 'number'
        ? obj['totalPages']
        : typeof obj['pageCount'] === 'number'
          ? obj['pageCount']
          : Math.max(1, Math.ceil(totalCount / pageSize));

    return { children, totalCount, totalPages };
  }

  return { children: [], totalCount: 0, totalPages: 1 };
}

/**
 * Hook para carregar os alunos (children) de um grupo do grid de matrículas.
 * Disparado sob demanda quando o grupo é expandido.
 *
 * @param filter - `childrenFilter` que vem dentro do parent
 * @param enabled - Se false, a query não é executada
 * @param page - Página atual (1-based), default 1
 */
export function useEnrollmentGridChildren(
  filter: CourseRegistrationEnrollmentChildrenFilterViewModel | undefined,
  enabled: boolean,
  page = 1,
) {
  const query = useGetApiV1CourseregistrationEnrollmentGridChildren(filterToParams(filter, page), {
    query: { enabled },
  });

  const { children, totalCount, totalPages } = enabled
    ? parseChildrenResponse(query.data, CHILDREN_PAGE_SIZE)
    : { children: [], totalCount: 0, totalPages: 1 };

  return {
    children,
    isLoading: query.isLoading && enabled,
    isFetching: query.isFetching && enabled,
    isError: query.isError,
    pageSize: CHILDREN_PAGE_SIZE,
    totalCount,
    totalPages,
  } as const;
}
